import type { CSSProperties } from 'react';
import { Link, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth.js';
import { Runs } from './routes/Runs.js';
import { RunDetail } from './routes/RunDetail.js';
import { RunLogs } from './routes/RunLogs.js';
import { Coverage } from './routes/Coverage.js';

const navLinkStyle = ({ isActive }: { isActive: boolean }): CSSProperties => ({
  color: isActive ? 'var(--brand)' : 'var(--fg-muted)',
  fontWeight: isActive ? 600 : 400,
  textDecoration: 'none',
  fontSize: '0.875rem',
  padding: '4px 10px',
  borderRadius: '6px',
});

export function App() {
  const { idToken, email, signIn, signOut, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="center-screen">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  if (!idToken) {
    return (
      <div className="center-screen">
        <div className="signin-card">
          <h1>Borg</h1>
          <p className="signin-subtitle">Run dashboard</p>
          <button onClick={signIn} className="google-btn">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="header-brand">
          <Link to="/" className="brand-link">
            <strong>Borg</strong>
          </Link>
          <nav className="app-nav">
            <NavLink to="/runs" style={navLinkStyle}>Runs</NavLink>
            <NavLink to="/coverage" style={navLinkStyle}>Coverage</NavLink>
          </nav>
        </div>
        <div className="header-who">
          <span className="who-email">{email}</span>
          <button onClick={signOut} className="btn-secondary">Sign out</button>
        </div>
      </header>
      <main className="app-main">
        <Routes>
          <Route path="/" element={<Navigate to="/runs" replace />} />
          <Route path="/runs" element={<Runs />} />
          <Route path="/runs/:id" element={<RunDetail />} />
          <Route path="/runs/:id/logs" element={<RunLogs />} />
          <Route path="/coverage" element={<Coverage />} />
          <Route path="*" element={<Navigate to="/runs" replace />} />
        </Routes>
      </main>
    </div>
  );
}
